import { Injectable } from '@angular/core';
import { CurrencyPipe, DecimalPipe, DatePipe } from '@angular/common';
import { ColumnDef } from '../models/common-model';
import { ColumnDataType, ColumnFilterValue } from './column-filter-dialog/column-filter-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class CommonDetailsService {

  private currencyPipe = new CurrencyPipe('en-US');
  private decimalPipe = new DecimalPipe('en-US');
  private datePipe = new DatePipe('en-US');

  getMonthShortName(month?: number): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (!month || month < 1 || month > 12) return '';
    return monthNames[month - 1];
  }

  getMonthName(month?: number): string {
    if (!month || month < 1 || month > 12) return 'N/A';
    return new Date(2000, month - 1, 1).toLocaleString(undefined, { month: 'long' });
  }

  getMonthOptions(format: 'long' | 'short' = 'long', year?: number): { value: number; label: string }[] {
    const now = new Date();
    const maxMonth = (year != null && year === now.getFullYear()) ? now.getMonth() + 1 : 12;
    return Array.from({ length: maxMonth }, (_, i) => {
      const date = new Date(2000, i, 1);
      return {
        value: i + 1,
        label: date.toLocaleString(undefined, { month: format })
      };
    });
  }

  formatValue(value: any, col: ColumnDef): string {
    if (value == null || value === '') return '';
    const dataType = (col.uiDataType || '').trim();
    if (!dataType) return value;

    const normalized = dataType.toLowerCase();
    const hasExplicitDigits = dataType.includes(':');
    const explicitDigits = dataType.split(':')[1] || '';

    if (normalized === 'date') {
      // Extract the date portion only (YYYY-MM-DD) and parse as local midnight
      // to avoid UTC-to-local timezone shifts rolling the date back by one day.
      const datePart = typeof value === 'string' && value.length >= 10
        ? value.substring(0, 10)
        : value;
      const dateValue = typeof datePart === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(datePart)
        ? datePart + 'T00:00:00'
        : value;
      return this.datePipe.transform(dateValue, 'yyyy-MM-dd') || value;
    }
    if (normalized === 'currency' || normalized.startsWith('currency:')) {
      const digits = hasExplicitDigits ? (explicitDigits || '2') : '2';
      return this.currencyPipe.transform(value, 'USD', 'symbol', '1.' + digits + '-' + digits) || value;
    }
    if (normalized === 'number' || normalized.startsWith('number:')) {
      const digits = hasExplicitDigits ? (explicitDigits || '0') : '0';
      return this.decimalPipe.transform(value, '1.' + digits + '-' + digits) || value;
    }
    if (normalized === 'percent' || normalized.startsWith('percent:')) {
      const digits = hasExplicitDigits ? (explicitDigits || '2') : '2';
      const formatted = this.decimalPipe.transform(value, '1.' + digits + '-' + digits);
      return formatted ? formatted + '%' : value;
    }
    return value;
  }

  formatByDataType(value: any, uiDataType?: string): string {
    return this.formatValue(value, { columnName: '', headerName: '', field: '', uiDataType });
  }

  getAlignment(col: ColumnDef, rows?: any[]): string {
    if (!col.uiAlignment && rows && rows.length > 0) {
      const values = rows.map(r => r[col.field]).filter(v => v != null && v !== '');
      if (values.length > 0 && values.every(v => {
        if (typeof v !== 'string') return false;
        const normalized = v.trim().toUpperCase();
        return normalized.length <= 1 || normalized === 'NA' || normalized === 'N/A';
      })) {
        col.uiAlignment = 'center';
      }
    }
    if (col.uiAlignment) return col.uiAlignment;
    const dataType = (col.uiDataType || '').toLowerCase();
    if (!dataType) return 'left';
    if (dataType === 'currency' || dataType.startsWith('currency:') || dataType === 'number' || dataType.startsWith('number:') || dataType === 'percent' || dataType.startsWith('percent:')) {
      return 'right';
    }
    return 'left';
  }

  shouldWrap(col: ColumnDef): boolean {
    if (col.uiAlignment === 'center') return false;
    return !col.uiDataType;
  }

  getColumnDataType(uiDataType?: string): ColumnDataType {
    const normalized = (uiDataType || '').trim().toLowerCase();
    if (!normalized) return 'text';
    if (normalized === 'date' || normalized === 'timestamp') return 'date';
    if (normalized === 'currency' || normalized.startsWith('currency:') ||
        normalized === 'number' || normalized.startsWith('number:') ||
        normalized === 'percent' || normalized.startsWith('percent:')) return 'number';
    return 'text';
  }

  compareValues(a: any, b: any, colType: ColumnDataType): number {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (colType === 'number') {
      return (Number(a) || 0) - (Number(b) || 0);
    }
    if (colType === 'date') {
      return new Date(a).getTime() - new Date(b).getTime();
    }
    return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
  }

  matchesColumnFilters(item: any, columnFilters: Map<string, ColumnFilterValue>): boolean {
    for (const [field, filterVal] of columnFilters.entries()) {
      const cellValue = String(item[field] ?? '');

      // Text filter
      if (filterVal.textValue) {
        const mode = filterVal.textFilterMode || 'search';
        const cell = cellValue.toLowerCase();
        const filter = filterVal.textValue.toLowerCase();
        if (mode === 'equals') {
          if (cell !== filter) return false;
        } else if (mode === 'regex') {
          try { if (!new RegExp(filterVal.textValue, 'i').test(cellValue)) return false; } catch { return false; }
        } else {
          if (!cell.includes(filter)) return false;
        }
      }

      // Select filter
      if (filterVal.selectValues && filterVal.selectValues.length > 0) {
        const lowerSet = new Set(filterVal.selectValues.map(v => v.toLowerCase()));
        if (!lowerSet.has(cellValue.toLowerCase())) return false;
      }

      // Date filter
      if (filterVal.datePreset) {
        const dateMs = new Date(cellValue).getTime();
        if (isNaN(dateMs)) return false;
        if (filterVal.dateFrom) {
          const from = new Date(filterVal.dateFrom);
          from.setHours(0, 0, 0, 0);
          if (dateMs < from.getTime()) return false;
        }
        if (filterVal.dateTo) {
          const to = new Date(filterVal.dateTo);
          to.setHours(23, 59, 59, 999);
          if (dateMs > to.getTime()) return false;
        }
      }

      // Number filter
      if (filterVal.numberFilterMode) {
        const num = Number(String(cellValue).replace(/[$,%\s]/g, ''));
        if (isNaN(num)) return false;
        const mode = filterVal.numberFilterMode;
        if (mode === 'between') {
          if (filterVal.numberMin != null && num < filterVal.numberMin) return false;
          if (filterVal.numberMax != null && num > filterVal.numberMax) return false;
        } else {
          const target = filterVal.numberValue;
          if (target != null) {
            if (mode === 'equals' && num !== target) return false;
            if (mode === 'gt' && num <= target) return false;
            if (mode === 'gte' && num < target) return false;
            if (mode === 'lt' && num >= target) return false;
            if (mode === 'lte' && num > target) return false;
          }
        }
      }
    }
    return true;
  }

  convertUTCToEST(utcDateString: string): string {
    if (!utcDateString) return '';
    
    try {
      // Ensure the date is treated as UTC
      let dateStr = utcDateString;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr += 'Z';
      }
      const utcDate = new Date(dateStr);
      if (isNaN(utcDate.getTime())) return utcDateString;

      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'America/New_York',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      const formatted = utcDate.toLocaleString('en-US', options);
      const [datePart, timePart] = formatted.split(', ');
      return `${datePart} | ${timePart}`;
      
    } catch (error) {
      console.error('Date conversion error:', error);
      return utcDateString;
    }
  }
 
  calcdate(daydiff: number, sourcedt?: Date): Date {
    let retDt = new Date();
    if (sourcedt) {
      retDt = sourcedt;
    }
    if (daydiff) {
      retDt.setDate(retDt.getDate() - daydiff);
    }
    return retDt;
  }

}
